import { FC, ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'
import { TSpace } from '@valuecase/common'
import { useReadSpace } from '../../hooks/useReadSpace'

interface SpaceSheetContextValue {
  selectedSpace: (TSpace & { data: any }) | null
  selectedSpaceId: string | null
  openSpaceSheet: (spaceId: string) => void
  closeSpaceSheet: () => void
}

const SpaceSheetContext = createContext<SpaceSheetContextValue | null>(null)

export const useSpaceSheet = () => {
  const context = useContext(SpaceSheetContext)
  if (!context) {
    throw new Error('useSpaceSheet must be used within SpaceSheetProvider')
  }
  return context
}

/**
 * This context controls the opening and closing of the space sheet (which contains the space sidebar) and which space is selected.
 */
export const SpaceSheetProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null)
  const { data: space } = useReadSpace(selectedSpaceId ?? '')
  const openSpaceSheet = useCallback(
    (spaceId: string) => {
      if (selectedSpaceId === spaceId) {
        setSelectedSpaceId(null)
      } else {
        setSelectedSpaceId(spaceId)
      }
    },
    [selectedSpaceId],
  )

  const closeSpaceSheet = useCallback(() => {
    setSelectedSpaceId(null)
  }, [])

  const value = useMemo(
    () => ({
      selectedSpace: space ?? null,
      selectedSpaceId,
      openSpaceSheet,
      closeSpaceSheet,
    }),
    [openSpaceSheet, closeSpaceSheet, space, selectedSpaceId],
  )

  return <SpaceSheetContext.Provider value={value}>{children}</SpaceSheetContext.Provider>
}
